import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Eye,
  EyeSlash,
  Scales,
  ArrowRight,
  CircleNotch,
} from '@phosphor-icons/react';
import { usePlatformAuthStore, type PlatformAdmin } from '@/features/platform-auth/store';
import { platformApi, getPlatformApiErrorMessage } from '@/lib/platformApi';

export default function PlatformLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const setSession = usePlatformAuthStore((s) => s.setSession);
  const navigate = useNavigate();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (isPending) return;
    setErrorMsg('');
    setIsPending(true);
    try {
      const res = await platformApi.post('/auth/login', { email, password });
      const data = res.data?.data as { accessToken: string; admin: PlatformAdmin };
      setSession({ accessToken: data.accessToken, admin: data.admin });
      navigate('/platform', { replace: true });
    } catch (err) {
      setErrorMsg(getPlatformApiErrorMessage(err, 'Invalid email or password'));
    } finally {
      setIsPending(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-plat-bg px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-plat-accent-solid to-plat-accent-solid-hover text-plat-on-accent shadow-lg shadow-plat-accent-solid/30">
            <Scales className="h-6 w-6" weight="bold" />
          </div>
          <div>
            <div className="text-2xl font-bold tracking-tight text-plat-fg">Platform Console</div>
            <div className="mt-0.5 text-sm text-plat-muted">Jurivoxa Internal — Super Admin</div>
          </div>
        </div>

        {/* Card */}
        <div className="rounded-xl border border-plat-border bg-plat-surface p-8 shadow-2xl">
          <div className="mb-6">
            <h2 className="text-[18px] font-semibold text-plat-fg">Sign in</h2>
            <p className="mt-1 text-sm text-plat-muted">
              Access is restricted to authorised platform administrators.
            </p>
          </div>

          {errorMsg && (
            <div className="mb-4 rounded-lg border border-plat-danger-line bg-plat-danger-soft px-4 py-3 text-sm text-plat-danger-text">
              {errorMsg}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-plat-secondary">
                Email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 w-full rounded-lg border border-plat-border bg-plat-bg px-3.5 text-sm text-plat-fg placeholder-plat-faint transition focus:border-plat-focus focus:outline-none focus:ring-2 focus:ring-plat-focus/50"
                placeholder="admin@jurivoxa.app"
              />
            </div>

            <div>
              <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-plat-secondary">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 w-full rounded-lg border border-plat-border bg-plat-bg px-3.5 pr-10 text-sm text-plat-fg placeholder-plat-faint transition focus:border-plat-focus focus:outline-none focus:ring-2 focus:ring-plat-focus/50"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-plat-faint hover:text-plat-secondary"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeSlash className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isPending}
              className="group inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-plat-accent-solid px-5 text-sm font-medium text-plat-on-accent transition hover:bg-plat-accent-solid-hover disabled:cursor-not-allowed disabled:bg-plat-neutral-soft disabled:text-plat-disabled"
            >
              {isPending ? (
                <>
                  <CircleNotch className="h-4 w-4 animate-spin" />
                  Signing in…
                </>
              ) : (
                <>
                  Sign in
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </button>

            <div className="text-center">
              <Link
                to="/platform/forgot-password"
                className="text-xs text-plat-muted underline-offset-4 transition hover:text-plat-secondary hover:underline"
              >
                Forgot your password?
              </Link>
            </div>
          </form>

          {import.meta.env.DEV && (
            <div className="mt-6 rounded-lg border border-dashed border-plat-border bg-plat-bg/60 p-3 text-xs text-plat-faint">
              <div className="mb-1 font-semibold text-plat-muted">Dev — Platform admin account</div>
              <div className="">admin@jurivoxa.local / ChangeMe123!</div>
            </div>
          )}
        </div>

        <div className="mt-6 text-center text-xs text-plat-secondary">
          © {new Date().getFullYear()} Jurivoxa · Platform Console
        </div>
      </div>
    </div>
  );
}
